import React, { useState, useEffect } from 'react';
import {
  Box, 
  Typography, 
  Button, 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  CircularProgress, 
  Divider, 
  Alert, 
  Grid,
  Chip,
  Container,
  Card,
  CardContent,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { runInvoiceTests } from './tests/invoiceTests';
import { runClientTests } from './tests/clientTests';
import { runBusinessTests } from './tests/businessTests';
import { runUITests } from './tests/uiTests';

// Define test result type
interface TestResult {
  name: string;
  status: 'success' | 'failure' | 'pending' | 'skipped';
  message?: string;
  details?: string;
  duration?: number;
}

interface TestSuite {
  name: string;
  tests: TestResult[];
  runAllTests: () => Promise<TestResult[]>;
  status: 'idle' | 'running' | 'completed';
  progress: number;
}

const TestRunner: React.FC = () => {
  const [testSuites, setTestSuites] = useState<TestSuite[]>([
    {
      name: 'Invoice Tests',
      tests: [],
      runAllTests: runInvoiceTests,
      status: 'idle',
      progress: 0
    },
    {
      name: 'Client Tests',
      tests: [],
      runAllTests: runClientTests,
      status: 'idle',
      progress: 0
    },
    {
      name: 'Business Tests',
      tests: [],
      runAllTests: runBusinessTests,
      status: 'idle',
      progress: 0
    },
    {
      name: 'UI Tests',
      tests: [],
      runAllTests: runUITests,
      status: 'idle',
      progress: 0
    }
  ]);
  
  const [isRunningAll, setIsRunningAll] = useState(false);
  const [summary, setSummary] = useState({
    total: 0,
    passed: 0,
    failed: 0,
    skipped: 0,
    pending: 0
  });

  // Calculate summary based on test results
  useEffect(() => {
    const newSummary = {
      total: 0,
      passed: 0,
      failed: 0,
      skipped: 0,
      pending: 0
    };
    
    testSuites.forEach(suite => {
      suite.tests.forEach(test => {
        newSummary.total++;
        if (test.status === 'success') newSummary.passed++;
        else if (test.status === 'failure') newSummary.failed++;
        else if (test.status === 'skipped') newSummary.skipped++;
        else if (test.status === 'pending') newSummary.pending++;
      });
    });
    
    setSummary(newSummary);
  }, [testSuites]);

  const runTestSuite = async (suiteIndex: number) => {
    // Create a copy of test suites
    const updatedTestSuites = [...testSuites];
    const suite = updatedTestSuites[suiteIndex];
    
    // Set status to running
    suite.status = 'running';
    suite.progress = 0;
    suite.tests = [];
    setTestSuites(updatedTestSuites);
    
    try {
      // Run all tests in the suite
      const results = await suite.runAllTests();
      
      // Update with results
      suite.tests = results;
      suite.status = 'completed';
      suite.progress = 100;
      setTestSuites([...updatedTestSuites]);
    } catch (error) {
      console.error(`Error running ${suite.name}:`, error);
      suite.status = 'completed';
      suite.progress = 100;
      setTestSuites([...updatedTestSuites]);
    }
  };

  const runAllTests = async () => {
    setIsRunningAll(true);
    
    // Reset all test suites
    const resetSuites = testSuites.map(suite => ({
      ...suite,
      tests: [],
      status: 'idle' as 'idle',
      progress: 0
    })) as TestSuite[];
    setTestSuites(resetSuites);
    
    // Run each test suite sequentially
    for (let i = 0; i < resetSuites.length; i++) {
      await runTestSuite(i);
    }
    
    setIsRunningAll(false);
  };

  const resetAllTests = () => {
    const resetSuites = testSuites.map(suite => ({
      ...suite,
      tests: [],
      status: 'idle' as 'idle',
      progress: 0
    })) as TestSuite[];
    setTestSuites(resetSuites);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            AkMo Accounts Testing Tool
          </Typography>
          <Box>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<PlayIcon />}
              onClick={runAllTests}
              disabled={isRunningAll}
              sx={{ mr: 2 }}
            >
              Run All Tests
            </Button>
            <Button 
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={resetAllTests}
              disabled={isRunningAll}
            >
              Reset
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Test Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    Total
                  </Typography>
                  <Typography variant="h4">
                    {summary.total}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={2.4}>
              <Card sx={{ bgcolor: '#e8f5e9' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="success.main" gutterBottom>
                    Passed
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {summary.passed}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={2.4}>
              <Card sx={{ bgcolor: '#ffebee' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="error.main" gutterBottom>
                    Failed
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {summary.failed}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={2.4}>
              <Card sx={{ bgcolor: '#fff8e1' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="warning.main" gutterBottom>
                    Skipped
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {summary.skipped}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={2.4}>
              <Card sx={{ bgcolor: '#e3f2fd' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="info.main" gutterBottom>
                    Pending
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {summary.pending}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        <Typography variant="h6" gutterBottom>
          Test Suites
        </Typography>
        {testSuites.map((suite, suiteIndex) => (
          <Accordion key={suite.name} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mr: 2 }}>
                    {suite.name}
                  </Typography>
                  
                  {suite.status === 'completed' && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip 
                        size="small" 
                        icon={<SuccessIcon fontSize="small" />} 
                        label={`${suite.tests.filter(t => t.status === 'success').length} passed`}
                        color="success"
                        variant="outlined"
                      />
                      {suite.tests.filter(t => t.status === 'failure').length > 0 && (
                        <Chip 
                          size="small" 
                          icon={<ErrorIcon fontSize="small" />} 
                          label={`${suite.tests.filter(t => t.status === 'failure').length} failed`}
                          color="error"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  )}
                </Box>
                
                <Box>
                  {suite.status === 'idle' && (
                    <Button 
                      size="small" 
                      variant="outlined" 
                      color="primary"
                      startIcon={<PlayIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        runTestSuite(suiteIndex);
                      }}
                    >
                      Run Tests
                    </Button>
                  )}
                  
                  {suite.status === 'running' && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      <Typography variant="body2">Running...</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {suite.status === 'running' && (
                <Box sx={{ width: '100%', mb: 2 }}>
                  <LinearProgress variant="determinate" value={suite.progress} />
                </Box>
              )}
              
              {suite.tests.length > 0 ? (
                <List dense>
                  {suite.tests.map((test, index) => (
                    <React.Fragment key={index}>
                      <ListItem 
                        sx={{ 
                          borderLeft: '4px solid',
                          borderLeftColor: 
                            test.status === 'success' ? 'success.main' :
                            test.status === 'failure' ? 'error.main' :
                            test.status === 'skipped' ? 'warning.main' : 'info.main',
                          pl: 2,
                          bgcolor: 
                            test.status === 'success' ? 'rgba(76, 175, 80, 0.08)' :
                            test.status === 'failure' ? 'rgba(244, 67, 54, 0.08)' :
                            test.status === 'skipped' ? 'rgba(255, 152, 0, 0.08)' : 'rgba(33, 150, 243, 0.08)',
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {test.status === 'success' && <SuccessIcon fontSize="small" color="success" sx={{ mr: 1 }} />}
                              {test.status === 'failure' && <ErrorIcon fontSize="small" color="error" sx={{ mr: 1 }} />}
                              {test.status === 'skipped' && <WarningIcon fontSize="small" color="warning" sx={{ mr: 1 }} />}
                              {test.status === 'pending' && <InfoIcon fontSize="small" color="info" sx={{ mr: 1 }} />}
                              <Typography variant="body1">{test.name}</Typography>
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              {test.message && (
                                <Typography variant="body2" color="textSecondary">
                                  {test.message}
                                </Typography>
                              )}
                              {test.duration !== undefined && (
                                <Typography variant="caption" color="textSecondary">
                                  Duration: {test.duration}ms
                                </Typography>
                              )}
                              {test.details && test.status === 'failure' && (
                                <Alert severity="error" sx={{ mt: 1 }}>
                                  <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.85rem' }}>
                                    {test.details}
                                  </pre>
                                </Alert>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < suite.tests.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {suite.status === 'idle' ? 'Click "Run Tests" to start testing' : 'No test results yet'}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
    </Container>
  );
};

export default TestRunner; 